import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import Image from 'next/image';
import Button from '@mui/material/Button';
import Subscribe from './Subscribe';
import RecommendJob from '@/components/RecommendJob';
import { JobTitleContext } from '@/context/create-context';
import { useClintMediaQuery } from '@/utils/hooks';
import { JOB_TITLE_PAGE_ROUTER_PATH } from '@/constants/router-path';
import { deduplicateObjListByKey } from '@/utils/common-utils';
import { useShowMore } from '@/utils/hooks';
import { trackClickEvent } from '@/utils/site-track';

const Summary = () => {
  const {
    displayName,
    jobTitle: {
      id: jobTitleId,
      name: jobTitleName,
      description,
      aliases: jobTitleAliasesName,
      city_name: cityName = '',
      salary_details: {
        avg,
        min,
        max,
        avg_median,
        median_avg,
        min_median,
        max_median,
        avg_value_by_experience_details: avgValueByExperienceDetails,
      },
      job_open_details: { current_count: currentCount },
      current_count_group_by_experience: currentCountByExperience,
      location_salary,
    },
  } = React.useContext(JobTitleContext);
  const isMobile = useClintMediaQuery();
  const [jobTitleAliasOpen, setJobTitleAliasOpen] = React.useState(false);
  const [showMore, setShowMore] = React.useState(true);
  const pageId = useParams()?.id as string;

  const jobTitleAliasRef = React.useRef<HTMLDivElement>(null);

  useShowMore(jobTitleAliasRef, (val) => setShowMore(val));

  const jobTitleAliases = React.useMemo(() => {
    const regx = /初级|中级|高级|资深/gi;
    const aliases = jobTitleAliasesName
      .filter((item) => item.name !== displayName)
      .map((item) => ({
        ...item,
        name: item.name.replace(regx, ''),
      }))
      .filter((item) => item.name !== displayName);
    return deduplicateObjListByKey(aliases, 'name');
  }, [jobTitleAliasesName, displayName]);

  return (
    <>
      <div className="flex flex-col tablet:justify-between tablet:items-center tablet:flex-row">
        <h1 className="text-5xl font-semibold leading-14">{`${cityName}${displayName}`}</h1>
        <Subscribe />
      </div>
      <div className="mt-6 leading-6">
        {cityName ? (
          <>
            {isMobile ? (
              <div className="block tablet:hidden">
                <Image
                  src={`https://tanzhi-prod.obs.cn-east-3.myhuaweicloud.com/job-titles/mobile-${pageId}.png`}
                  alt="description"
                  width={0}
                  height={0}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            ) : (
              <div className="hidden tablet:block">
                <Image
                  src={`https://tanzhi-prod.obs.cn-east-3.myhuaweicloud.com/job-titles/${pageId}.png`}
                  alt="description"
                  width={0}
                  height={0}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <p>{description}</p>
        )}
      </div>
      <div className="bg-white rounded-lg mt-10 mb-3 tablet:mb-3">
        <RecommendJob
          avg={avg}
          min={min}
          max={max}
          avgMedian={avg_median}
          medianAvg={median_avg}
          minMedian={min_median}
          maxMedian={max_median}
          avgValueByExperienceDetails={avgValueByExperienceDetails}
          currentCountByExperience={currentCountByExperience}
          currentCount={currentCount}
        />
      </div>
      {(jobTitleAliases.length !== 0 || jobTitleName !== displayName) && (
        <div
          ref={jobTitleAliasRef}
          data-open={isMobile || jobTitleAliasOpen}
          className="text-base text-black flex flex-wrap items-center mb-10 gap-1.5 overflow-hidden relative h-8 data-[open=true]:h-auto tablet:text-xs tablet:leading-5 tablet:mb-8 tablet:pr-20"
        >
          与{`${cityName}${displayName}`}
          相似名称：
          {jobTitleName !== displayName && (
            <Link
              href={`${
                cityName
                  ? `/${location_salary?.pinyin}/`
                  : JOB_TITLE_PAGE_ROUTER_PATH
              }${jobTitleId}/`}
              className="rounded-lg px-2.5 py-2 bg-white text-xs font-normal hover:text-web-blue-500"
              data-ele-group="similar-job"
              data-ele-id={`job_${jobTitleName}`}
              data-observer-id={`similar-job_job_${jobTitleName}`}
              onClick={trackClickEvent}
            >
              {`${cityName}${jobTitleName}`}
            </Link>
          )}
          {jobTitleAliases.map(({ id, name }, index) => (
            <React.Fragment key={id + index}>
              {id ? (
                <Link
                  href={`${
                    cityName
                      ? `/${location_salary?.pinyin}/`
                      : JOB_TITLE_PAGE_ROUTER_PATH
                  }${id}/`}
                  className="rounded-lg px-2.5 py-2 bg-white text-xs font-normal hover:text-web-blue-500"
                  data-ele-group="similar-job"
                  data-ele-id={`job_${name}`}
                  data-observer-id={`similar-job_job_${name}`}
                  onClick={trackClickEvent}
                >
                  {`${cityName}${name}`}
                </Link>
              ) : (
                <div className="rounded-lg px-2.5 py-2 bg-white text-xs font-normal">
                  {`${cityName}${name}`}
                </div>
              )}
            </React.Fragment>
          ))}
          <Button
            data-show={!isMobile && showMore}
            variant="text"
            className="!text-xs !absolute !right-0 !top-0.5 data-[show=false]:!hidden"
            data-ele-group="similar-job"
            data-ele-id={`check-more`}
            data-observer-id={`similar-job_check-more`}
            onClick={(e) => {
              setJobTitleAliasOpen(!jobTitleAliasOpen);
              !jobTitleAliasOpen && trackClickEvent(e);
            }}
          >
            {jobTitleAliasOpen ? '收起' : '查看更多'}
          </Button>
        </div>
      )}
    </>
  );
};

export default Summary;
